import { FC, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { Link } from "@/components/Interface/Link/Link";
import { Notification } from "@/components/Interface/Notification/Notification";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { BillingAccountModal } from "@/components/Portal/Documents/BillingAccounts/BillingAccountModal/BillingAccountModal";
import { BillingAccountPickerModal } from "@/components/Portal/Documents/BillingAccounts/BillingAccountPicker/BillingAccountPickerModal";
import { BillingAccountSelectField } from "@/components/Portal/Documents/BillingAccounts/BillingAccountPicker/BillingAccountSelectField";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { DrAccount } from "@/services/model";

type OpenModal = "picker" | "create" | null;

interface Props {
  accounts: DrAccount[];
  selectedId?: number;
  disabled?: boolean;
  onChange: (accountId: number) => void;
}

/** "Billing": the billing account the new subscription is billed to. */
export const CheckoutBillingAccount: FC<Props> = ({
  accounts,
  selectedId,
  disabled,
  onChange,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "Onboarding.sections.checkout.billingAccount",
  });
  const { t: tCommon } = useTranslation();
  const [openModal, setOpenModal] = useState<OpenModal>(null);
  const selectedAccount = accounts.find(
    (account) => account.id != null && account.id === selectedId,
  );

  return (
    <CardModule
      dataTestid="checkout-billing-account"
      variant="white"
      title={t("title")}
      description={t("description")}
    >
      {selectedAccount ? (
        <BillingAccountSelectField
          dataTestid="checkout-billing-account-select"
          account={selectedAccount}
          disabled={disabled}
          onClick={() => setOpenModal("picker")}
        />
      ) : (
        <Button
          dataTestid="checkout-billing-account-choose"
          fullWidth
          disabled={disabled}
          onClick={() => setOpenModal("picker")}
        >
          {tCommon("portal:user-account.billingAccounts.picker.title")}
        </Button>
      )}
      {openModal === "picker" && (
        <BillingAccountPickerModal
          accounts={accounts}
          selectedId={selectedId}
          hint={
            <Notification
              dataTestid="checkout-billing-account-hint"
              inverted
              description={
                <Trans
                  i18nKey="Onboarding.sections.checkout.billingAccount.editHint"
                  components={[
                    <Link
                      key="billing-accounts-link"
                      inverted
                      size="sm"
                      to={portalRoutes.documents.payments}
                    />,
                  ]}
                />
              }
            />
          }
          onSelect={(accountId) => {
            onChange(accountId);
            setOpenModal(null);
          }}
          onCreate={() => setOpenModal("create")}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "create" && (
        <BillingAccountModal
          onClose={() => setOpenModal(null)}
          onCreated={onChange}
        />
      )}
    </CardModule>
  );
};
